import React, { memo, useMemo, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { Container } from 'reactstrap';
import { CommonService, CourseService } from '@business/services';
import { OrgChartBlockSettings } from './OrgChartBlockSettings';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { DiagramViewer, DiagramViewerHandle } from '@modules/common/Diagram';
import { R } from '@core/utils/r';

function defineOrgChartBlockBlockForm(variation: OrgChartBlockVariation) {
  return formGroup({
    heading: formControl<string>({ initialValue: '' }),
    text: formControl<string>({
      initialValue: `<ol><li>Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</li><li>Penatibus libero risus montes conubia pretium quisque ut fermentum, vulputate augue litora ornare fames turpis convallis imperdiet, curae etiam sed enim dictumst purus sit.</li><li>Tortor parturient gravida habitant dapibus et fusce felis nam nibh litora ridiculus aenean porttitor turpis pharetra.</li><li>Suscipit montes natoque aptent ex gravida orci ultrices volutpat nunc.</li></ol>`,
    }),
    id: formControl<string>({
      label: R.startCase(variation),
    }),
    advance: defineBlockAdvanceForm({ paddingTop: 0, paddingBottom: 0 }),
  });
}

export type OrgChartBlockWidgetFormData = FormDataType<typeof defineOrgChartBlockBlockForm>;
export type OrgChartBlockWidgetForm = ReturnType<typeof defineOrgChartBlockBlockForm>;

export enum OrgChartBlockVariation {
  orgChart = 'orgChart',
}

export const OrgChartBlock = memo((props: CourseBuilder.BlockProps<OrgChartBlockVariation>) => {
  const { variation, id } = props;
  const form = useMemo(() => defineOrgChartBlockBlockForm(variation), [variation]);
  const formValue = useNonNilObservable(form.value$);
  const viewerHandle = useRef<DiagramViewerHandle>(null);

  const content = useMemo(() => {
    if (CommonService.isJson(formValue.text)) {
      return JSON.parse(formValue.text);
    }
    return null;
  }, [formValue.text]);

  const onFormChange = useCallback((data: string) => {
    if (CommonService.isJson(data)) {
      viewerHandle.current?.onData(JSON.parse(data));
    }
  }, []);

  useConfigureBlock({
    props,
    form,
    contentComponent: OrgChartBlockSettings,
    contentProps: { form, variation, onFormChange },
  });

  return (
    <BlockContainer className={classNames('d-flex flex-fill', 'block-list', variation)} advanceForm={formValue.advance}>
      <Container>
        {!R.isEmpty(formValue.heading) && <h3>{formValue.heading}</h3>}
        {R.isArray(content) && !!content.length && (
          <DiagramViewer noHeight handleRef={viewerHandle} id={'viewer-' + id} data={content} type={variation} />
        )}

        {(!content || (content && !R.isArray(content))) && (
          <DiagramViewer noHeight handleRef={viewerHandle} id={'viewer-' + id} data={CourseService.getDummyOrgChartData()} type={variation} />
        )}
      </Container>
    </BlockContainer>
  );
});
